// src/pages/ExhaustProduct.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { EXHAUST_PRODUCTS } from "../data/exhaustProducts";
import { DOWNPIPE_PRODUCTS } from "../data/downpipeProducts";

/* ---------------- TYPE / SLUG HELPERS ---------------- */
const isDownpipe = (p) => /^ul-dp-/i.test(p?.slug || "");

// exhaust -> downpipe slug (you already had this)
function exhaustToDpSlug(exhSlug = "") {
  // handles ul-catback-xxx and ul-valved-xxx
  const rest = exhSlug.replace(/^ul-(catback|valved|axle)-/i, "");
  return `ul-dp-${rest}`;
}

// family-correct primary thumbnail for any product
function getPrimaryThumb(p) {
  if (!p) return "";
  const dp = isDownpipe(p);
  const idNum = String(p.id).replace(/\D/g, "");
  const padded = idNum.padStart(2, "0");
  const prefix = dp ? "p-dp" : "p-exh";
  const base = `/assets/${prefix}-${padded}.jpg`;

  // only trust product.img if it matches its own family
  const looksRight = p?.img && new RegExp(`/${prefix}-`, "i").test(p.img);
  return looksRight ? p.img : base;
}

/* ---------------- BUILD MEDIA LIST ----------------
   Type-aware: DP pages use p-dp-XX.* and will never fall back to p-exh-XX.*.
   Exhaust pages keep using p-exh-XX.*.
---------------------------------------------------- */
function getMediaSet(product) {
  if (!product) return Promise.resolve([]);

  const dp = isDownpipe(product);
  const index = String(product.id).replace(/\D/g, "");
  const padded = index.padStart(2, "0");
  const prefix = dp ? "p-dp" : "p-exh";

  const base = `/assets/${prefix}-${padded}.jpg`;

  // only use product.img if it belongs to the right family
  const wrongFamily =
    product?.img &&
    ((dp && /\/p-exh-/i.test(product.img)) || (!dp && /\/p-dp-/i.test(product.img)));

  const candidates = [
    product?.img && !wrongFamily && { type: "image", src: product.img },
    (!product?.img || wrongFamily) && { type: "image", src: base },

    // images (keep your generous range)
    { type: "image", src: `/assets/${prefix}-${padded}-1.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-2.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-3.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-4.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-5.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-6.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-7.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-8.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-9.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-10.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-11.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-12.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-13.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-14.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-15.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-16.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-17.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-18.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-19.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-20.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-21.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-22.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-23.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-24.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-25.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-26.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-27.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-28.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-29.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-30.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-31.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-32.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-33.jpg` },
    { type: "image", src: `/assets/${prefix}-${padded}-34.jpg` },

    // videos (poster=type-correct base/img)
    {
      type: "video",
      src: `/assets/${prefix}-${padded}-clip1.mp4`,
      poster: product?.img && !wrongFamily ? product.img : base,
    },
    {
      type: "video",
      src: `/assets/${prefix}-${padded}-clip2.mp4`,
      poster: product?.img && !wrongFamily ? product.img : base,
    },
    {
      type: "video",
      src: `/assets/${prefix}-${padded}-clip3.mp4`,
      poster: product?.img && !wrongFamily ? product.img : base,
    },
    {
      type: "video",
      src: `/assets/${prefix}-${padded}-clip4.mp4`,
      poster: product?.img && !wrongFamily ? product.img : base,
    },
  ].filter(Boolean);

  // HEAD-check each candidate so broken files are removed
  const checks = candidates.map(
    (m) =>
      new Promise((resolve) => {
        if (m.type === "image") {
          const img = new Image();
          img.src = m.src;
          img.onload = () => resolve(m);
          img.onerror = () => resolve(null);
        } else {
          fetch(m.src, { method: "HEAD" })
            .then((res) => (res.ok ? resolve(m) : resolve(null)))
            .catch(() => resolve(null));
        }
      })
  );

  return Promise.all(checks)
    .then((results) => results.filter(Boolean))
    // de-dupe
    .then((arr) => arr.filter((m, i) => i === arr.findIndex((x) => x.type === m.type && x.src === m.src)));
}

/* ---------------- PRICING ---------------- */
const PRICING = {
  material: { stainless: 0, titanium: 900 },
  mufflerValved: { none: 0, valved: 450 },
  tips: { "carbon fiber": 0, silver: 0, gold: 0, purple: 0, blue: 0 },
};

export default function ExhaustProduct({ onAddToCart }) {
  const { slug } = useParams();
  const navigate = useNavigate();

  const product = EXHAUST_PRODUCTS.find((p) => p.slug === slug) || null;

  // Cross-sell: match to Downpipes (same model)
  const relatedDpSlug = exhaustToDpSlug(slug);
  const relatedDp = DOWNPIPE_PRODUCTS.find((d) => d.slug === relatedDpSlug);

  // C8-only cross-pipe option
  const [crossPipe, setCrossPipe] = useState("X-pipe");
  const showCrossPipe = product?.id === "p36";
  const crossPipeOptions = [
    { value: "X-pipe", label: "X-Pipe (Formula F1 Sound)" },
    { value: "Z-pipe", label: "Z-Pipe (Deep American Muscle Sound/chop)" },
  ];

  const [active, setActive] = useState(0);
  const [gallery, setGallery] = useState([]);
  const [material, setMaterial] = useState("stainless");
  const [mufflerValved, setMufflerValved] = useState("none");
  const [tips, setTips] = useState("carbon fiber");

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // ✅ NEW: dynamic aspect ratio for main media viewer (prevents 9:16 videos being forced into 16:9)
  const [activeAspect, setActiveAspect] = useState(null);

  const setAspectFromImage = (e) => {
    const w = e.currentTarget.naturalWidth;
    const h = e.currentTarget.naturalHeight;
    if (w && h) setActiveAspect(`${w} / ${h}`);
  };

  const setAspectFromVideo = (e) => {
    const w = e.currentTarget.videoWidth;
    const h = e.currentTarget.videoHeight;
    if (w && h) setActiveAspect(`${w} / ${h}`);
  };

  useEffect(() => {
    let alive = true;
    if (product) {
      getMediaSet(product).then((media) => {
        if (!alive) return;
        setGallery(media);
        if (media.length > 0) setActive(0);
      });
    } else {
      setGallery([]);
    }
    return () => {
      alive = false;
    };
  }, [product]);

  // ✅ NEW: reset aspect while switching media so we don’t “stick” old ratios
  useEffect(() => {
    setActiveAspect(null);
  }, [active]);

  if (!product) {
    return (
      <section className="mx-auto max-w-6xl px-4 py-20">
        <p className="text-neutral-600 mb-6">Product not found.</p>
        <button className="px-4 py-2 rounded bg-black text-white" onClick={() => navigate("/exhaust")}>
          &larr; Back to Exhausts
        </button>
      </section>
    );
  }

  const basePrice = product.basePrice ?? 0;
  const total =
    basePrice +
    (PRICING.material[material] || 0) +
    (PRICING.mufflerValved[mufflerValved] || 0) +
    (PRICING.tips[tips] || 0);

  const addToCart = () => {
    const idNum = String(product.id).replace(/\D/g, "").padStart(2, "0");
    const fallbackImg = `/assets/p-exh-${idNum}.jpg`;
    const correctImageUrl = gallery[0]?.src || product.img || fallbackImg;

    const payload = {
      id: product.id,
      slug: product.slug,
      name: `${product.name} Exhaust${showCrossPipe ? ` — ${crossPipe}` : ""}`,
      imageUrl: correctImageUrl,
      quantity: 1,
      options: { material, mufflerValved, tips, ...(showCrossPipe ? { crossPipe } : {}) },
      price: total,
      category: "exhaust",
    };
    onAddToCart?.(payload);
  };

  // Safe image for cross-sell quick-add (forces p-dp prefix)
  const dpThumb = relatedDp ? getPrimaryThumb(relatedDp) : "/assets/downpipe.jpg";

  return (
    <section className="mx-auto max-w-7xl px-4 py-10">
      <button className="text-sm text-neutral-500 hover:text-neutral-800 mb-6" onClick={() => navigate("/exhaust")}>
        &larr; Back to Exhausts
      </button>

      <div className="grid lg:grid-cols-2 gap-10">
        {/* ---------- LEFT: GALLERY ---------- */}
        <div>
          {/* ✅ UPDATED: no forced 16:9. Container adapts to actual media ratio. */}
          <div
            className="w-full overflow-hidden rounded-2xl bg-neutral-100 flex items-center justify-center cursor-zoom-in"
            style={{
              aspectRatio: activeAspect || "16 / 9", // fallback while media loads
              maxHeight: "70vh", // keeps tall 9:16 clips from swallowing the whole page
            }}
            onClick={() => {
              if (gallery.length > 0) {
                setLightboxIndex(active);
                setLightboxOpen(true);
              }
            }}
          >
            {gallery[active]?.type === "video" ? (
              <video
                key={gallery[active].src}
                src={gallery[active].src}
                poster={gallery[active].poster}
                controls
                playsInline
                onLoadedMetadata={setAspectFromVideo}
                className="w-full h-full object-contain bg-black"
              />
            ) : (
              <img
                src={gallery[active]?.src}
                alt={product.name}
                onLoad={setAspectFromImage}
                className="w-full h-full object-contain bg-black"
              />
            )}
          </div>

          {/* Thumbnails */}
          <div className="mt-4 flex flex-wrap gap-3">
            {gallery.map((m, i) => (
              <button
                key={m.src + i}
                onClick={() => setActive(i)}
                onDoubleClick={() => {
                  setLightboxIndex(i);
                  setLightboxOpen(true);
                }}
                className={`relative overflow-hidden rounded-xl border ${
                  i === active ? "border-black" : "border-neutral-200"
                } bg-white w-20 h-20 flex items-center justify-center cursor-zoom-in`}
              >
                {m.type === "video" ? (
                  <div className="relative w-full h-full">
                    <img
                      src={m.poster || gallery[0]?.src}
                      alt={`thumb-${i}`}
                      className="w-full h-full object-cover opacity-80"
                    />
                    <div className="absolute inset-0 flex items-center justify-center text-white text-xs font-bold bg-black/40">
                      ▶ VIDEO
                    </div>
                  </div>
                ) : (
                  <img src={m.src} alt={`thumb-${i}`} className="w-full h-full object-cover" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ---------- RIGHT: DETAILS ---------- */}
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold">{product.name}</h1>
            <p className="text-neutral-600 mt-2">
              Unlimited Levels performance exhaust handcrafted for your {product.name}. Choose between Stainless Steel or
              Titanium construction, optional muffler &amp; valves, and multiple tip finishes to suit your build.
            </p>

            <h3 className="text-xl font-semibold mt-6">Details</h3>
            <ul className="list-disc pl-6 mt-2 text-neutral-700 space-y-1">
              <li>Precision TIG-welded, direct-fit system</li>
              <li>Optimized flow for power and tone</li>
              <li>Bolt-on install; hardware included (varies by model)</li>
              <li>Choose between Stainless Steel or Titanium</li>
              <li>Choose Muffler &amp; Valves if desired (varies by model)</li>
              <li>Pick your exhaust tips — on us</li>
              <li>Built to order; most systems crafted within 1 week</li>
            </ul>
          </div>

          {/* ---------- OPTIONS ---------- */}
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-semibold mb-1">Exhaust Material</label>
              <select value={material} onChange={(e) => setMaterial(e.target.value)} className="w-full border rounded-lg px-3 py-2">
                <option value="stainless">Stainless Steel (Base Price)</option>
                <option value="titanium">Titanium (+$900)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1">Muffler/Valved</label>
              <select
                value={mufflerValved}
                onChange={(e) => setMufflerValved(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="none">No Muffler (Base Price)</option>
                <option value="valved">w/Muffler &amp; Valves ($450)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1">Tips</label>
              <select value={tips} onChange={(e) => setTips(e.target.value)} className="w-full border rounded-lg px-3 py-2">
                {["carbon fiber", "silver", "gold", "purple", "blue"].map((t) => (
                  <option key={t} value={t}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* --------- C8 Cross Pipe (X vs Z) ---------- */}
          {showCrossPipe && (
            <div className="mt-6">
              <label htmlFor="crossPipe" className="block text-sm font-semibold mb-2">
                Cross Pipe Version
              </label>
              <select
                id="crossPipe"
                value={crossPipe}
                onChange={(e) => setCrossPipe(e.target.value)}
                className="w-full rounded-xl border border-neutral-300 px-3 py-2"
              >
                {crossPipeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-neutral-500 mt-2">Applies only to the Corvette C8 exhaust.</p>
            </div>
          )}

          {/* ---------- PRICE + ADD ---------- */}
          <div className="pt-2">
            <div className="text-2xl font-semibold mb-4">Total: ${total.toLocaleString()}</div>
            <button
              onClick={addToCart}
              className="w-full md:w-auto inline-flex items-center justify-center px-5 py-3 rounded-xl bg-black text-white hover:bg-neutral-800"
            >
              ADD TO CART
            </button>

            {/* Cross-sell: Pairs perfectly with (Downpipes) */}
            {relatedDp && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-3">Pairs perfectly with</h3>

                <div className="flex items-center gap-4 rounded-2xl border border-neutral-200 p-3">
                  <img
                    src={getPrimaryThumb(relatedDp)} // ✅ family-correct DP image
                    alt={relatedDp.name}
                    className="w-24 h-24 object-cover rounded-xl border border-neutral-200"
                  />
                  <div className="flex-1">
                    <div className="font-medium">{relatedDp.name} — Downpipes</div>
                    <div className="text-sm text-neutral-600">From ${Number(relatedDp.basePrice || 0).toLocaleString()}</div>
                    <div className="mt-2 flex gap-2">
                      <button
                        className="px-3 py-2 text-sm rounded-lg border border-neutral-300 hover:bg-neutral-50"
                        onClick={() => navigate(`/downpipes/${relatedDp.slug}`)}
                      >
                        View Downpipes
                      </button>
                      <button
                        className="px-3 py-2 text-sm rounded-lg bg-black text-white hover:bg-neutral-800"
                        onClick={() => {
                          onAddToCart?.({
                            id: `dp_${relatedDp.slug}_RaceCatless`,
                            name: `${relatedDp.name} — Downpipes`,
                            price: Number(relatedDp.basePrice || 0),
                            imageUrl: getPrimaryThumb(relatedDp),
                            options: { catalyst: "Race Catless" },
                            slug: relatedDp.slug,
                            category: "downpipes",
                          });
                        }}
                      >
                        Add Downpipes (base)
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <p className="text-xs text-neutral-400 mt-2">
              Base: ${basePrice.toLocaleString()} • Titanium +$900 • w/Muffler &amp; Valves +$450 • Tip color — no charge
            </p>
          </div>
        </div>
      </div>

      {/* Lightbox (full-size uncropped media) */}
      {lightboxOpen && (
        <Lightbox media={gallery} index={lightboxIndex} onClose={() => setLightboxOpen(false)} setIndex={setLightboxIndex} />
      )}
    </section>
  );
}

/* ======================== Lightbox ======================== */
function Lightbox({ media, index, onClose, setIndex }) {
  const item = media[index];

  React.useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIndex((i) => Math.min(i + 1, media.length - 1));
      if (e.key === "ArrowLeft") setIndex((i) => Math.max(i - 1, 0));
    }
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [media.length, onClose, setIndex]);

  if (!item) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" onClick={onClose} role="dialog" aria-modal="true">
      <div className="relative max-w-[95vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        {/* Close */}
        <button className="absolute -top-10 right-0 px-3 py-1 rounded-md bg-white/10 text-white text-sm hover:bg-white/20" onClick={onClose}>
          Close ✕
        </button>

        {/* Prev / Next */}
        {index > 0 && (
          <button
            className="absolute left-0 -translate-x-full top-1/2 -translate-y-1/2 px-3 py-2 rounded-md bg-white/10 text-white hover:bg-white/20"
            onClick={() => setIndex((i) => Math.max(i - 1, 0))}
          >
            ‹
          </button>
        )}
        {index < media.length - 1 && (
          <button
            className="absolute right-0 translate-x-full top-1/2 -translate-y-1/2 px-3 py-2 rounded-md bg-white/10 text-white hover:bg-white/20"
            onClick={() => setIndex((i) => Math.min(i + 1, media.length - 1))}
          >
            ›
          </button>
        )}

        {/* Uncropped media */}
        {item.type === "video" ? (
          <video src={item.src} poster={item.poster} controls playsInline className="max-w-[95vw] max-h-[90vh] object-contain" />
        ) : (
          <img src={item.src} alt="" className="max-w-[95vw] max-h-[90vh] object-contain" />
        )}
      </div>
    </div>
  );
}
